import * as React from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import { useMemo } from "react";
import { PRIMARY_COLORS, PRIMARY_SILVER_COLOR } from "@/index";
import {
  Chip,
  styled,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Label, TextField as ErrorField } from "ui-components-library";
import ClearIcon from "@mui/icons-material/Clear";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const StyledBadgeChip = styled(Chip)<{
  chipColor?: string;
  chipBackground?: string;
}>(
  ({ chipColor = "white", chipBackground = PRIMARY_COLORS.PRIMARY_GREEN }) => ({
    backgroundColor: chipBackground,
    color: chipColor,
    "& .MuiChip-deleteIcon": {
      color: chipColor,
      opacity: 0.75,
      "&:hover": {
        color: chipColor,
        opacity: 1,
      },
    },
  }),
);

export const MarketsAutocomplete = ({
  data,
  handleMarketsChange,
  currentMarketTags,
  error,
  helperText,
  disabled,
  loading,
}) => {
  const [searchText, setSearchText] = React.useState("");

  const marketOptions = useMemo(
    () =>
      data
        .map((region) =>
          region?.child_tags?.map((country) =>
            country?.child_tags?.map(
              (state) =>
                state?.child_tags?.map((market) => ({
                  id: market.id,
                  name: market.name,
                  hierarchy: `${region.name} > ${country.name} > ${state.name}`,
                })) || [],
            ),
          ),
        )
        .flat(3)
        .filter(Boolean),
    [data],
  );

  const filteredOptions = useMemo(
    () =>
      marketOptions.filter(
        (market) =>
          market.name.toLowerCase().includes(searchText.toLowerCase()) ||
          market.hierarchy.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [marketOptions, searchText],
  );

  const handleChange = (event: SelectChangeEvent<typeof currentMarketTags>) => {
    const {
      target: { value },
    } = event;
    let newValue =
      typeof value === "string" ? value.split(",").map(Number) : value;

    newValue = newValue.filter((val) => typeof val === "number" && !isNaN(val));

    handleMarketsChange(newValue);
  };

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 5.9 + ITEM_PADDING_TOP,
        marginTop: "-5px",
        width: 400,
      },
    },
    anchorOrigin: {
      vertical: "top" as const,
      horizontal: "left" as const,
    },
    transformOrigin: {
      vertical: "bottom" as const,
      horizontal: "left" as const,
    },
    autoFocus: false,
  };

  const handleRemoveOption = (e, optionValue) => {
    const newSelected = currentMarketTags.filter(
      (value) => value !== optionValue,
    );
    handleMarketsChange(newSelected);
  };

  const handleSelectAll = () => {
    const filteredIds = filteredOptions.map((option) => option.id);
    const allFilteredSelected = filteredIds.every((id) =>
      currentMarketTags.includes(id),
    );

    let newSelected;
    if (allFilteredSelected) {
      newSelected = currentMarketTags.filter((id) => !filteredIds.includes(id));
    } else {
      const newIds = filteredIds.filter(
        (id) => !currentMarketTags.includes(id),
      );
      newSelected = [...currentMarketTags, ...newIds];
    }

    handleMarketsChange(newSelected);
  };

  const isAllSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => currentMarketTags.includes(option.id));

  const isIndeterminate =
    filteredOptions.length > 0 &&
    filteredOptions.some((option) => currentMarketTags.includes(option.id)) &&
    !isAllSelected;

  return (
    <Box>
      <FormControl sx={{ width: "100%" }} error={error}>
        <Label label="Select Markets">
          <Select
            labelId="markets-select-label"
            id="markets-select"
            multiple
            displayEmpty
            value={currentMarketTags}
            onChange={handleChange}
            input={
              <OutlinedInput
                endAdornment={
                  currentMarketTags.length > 0 && (
                    <ClearIcon
                      sx={{
                        marginRight: "20px",
                        ":hover": {
                          backgroundColor: "#EBEEEE",
                          cursor: "pointer",
                          borderRadius: "50%",
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleMarketsChange([]);
                      }}
                    />
                  )
                }
              />
            }
            sx={{
              "& .MuiSelect-select": {
                paddingTop: "5px",
                paddingBottom: "5px",
              },
              // temporary fix, need to find what is component's class is responsible for this
              ".css-1rwwqow-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input.css-1rwwqow-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input.css-1rwwqow-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input":
                {
                  paddingRight: 0,
                },
            }}
            renderValue={(value) => {
              if (loading || (value.length > 0 && marketOptions.length === 0))
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography
                      sx={{ color: "text.secondary", fontSize: "20px" }}
                    >
                      Loading...
                    </Typography>
                  </Box>
                );

              if (value.length === 0) {
                return (
                  <Typography
                    sx={{ color: "text.secondary", fontSize: "20px" }}
                  >
                    Select From List
                  </Typography>
                );
              }

              return (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    maxHeight: "112px",
                    overflowY: "auto",
                    gap: 0.5,
                  }}
                >
                  {value.map((marketId) => {
                    const market = marketOptions.find((m) => m.id === marketId);

                    if (!market) return null;

                    return (
                      <StyledBadgeChip
                        key={marketId}
                        label={market.name}
                        chipColor={"#fff"}
                        chipBackground={"#778F9C"}
                        sx={{ height: "25px", fontSize: ".875rem" }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onDelete={(e) => handleRemoveOption(e, marketId)}
                      />
                    );
                  })}
                </Box>
              );
            }}
            MenuProps={MenuProps}
            onClose={() => setSearchText("")}
            disabled={disabled || loading}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 1,
                borderBottom: `1px solid ${PRIMARY_SILVER_COLOR[250]}`,
                display: "flex",
                alignItems: "center",
              }}
            >
              <SearchIcon sx={{ marginLeft: "8px" }} />
              <TextField
                size="small"
                autoFocus
                placeholder="Search..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                  },
                }}
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </Box>
            <Box
              component="div"
              sx={{
                borderBottom: `1px solid ${PRIMARY_SILVER_COLOR[250]}`,
                cursor: "pointer",
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectAll();
              }}
            >
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
              />
              Select All
            </Box>
            {filteredOptions
              .sort((a, b) => {
                const aSelected = currentMarketTags.includes(a.id);
                const bSelected = currentMarketTags.includes(b.id);

                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;

                return a.name.localeCompare(b.name);
              })
              .map((market) => (
                <MenuItem
                  key={market.id}
                  value={market.id}
                  sx={{
                    borderBottom: `1px solid ${PRIMARY_SILVER_COLOR[250]}`,
                  }}
                >
                  <Checkbox checked={currentMarketTags.includes(market.id)} />
                  <ListItemText
                    primary={
                      <Box>
                        <Box sx={{ fontWeight: "medium" }}>{market.name}</Box>
                        <Box
                          sx={{ fontSize: "0.8em", color: "text.secondary" }}
                        >
                          {market.hierarchy}
                        </Box>
                      </Box>
                    }
                  />
                </MenuItem>
              ))}
            {filteredOptions.length === 0 && (
              <MenuItem disabled>
                <ListItemText primary="No markets found" />
              </MenuItem>
            )}
          </Select>
        </Label>
      </FormControl>
      {helperText && <ErrorField children={helperText} />}
    </Box>
  );
};
